import React, { useState } from "react";
import { Modal } from "@mui/material";
import { XIcon, CreditCardIcon } from "@heroicons/react/solid";
import { Button } from "../../components/ui";
import InlineLoader from "../../components/loader/InlineLoader";

const FundRequestMarkAsPaidModal = ({ open, onClose, request, isPaying, onMarkAsPaid }) => {
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");

  React.useEffect(() => {
    if (request && open) {
      setPaymentReference("");
      setPaymentRemarks("");
    }
  }, [request, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!request?.request_id) return;

    onMarkAsPaid({
      request_id: request.request_id,
      payment_reference: paymentReference.trim(),
      payment_remarks: paymentRemarks.trim(),
    });
  };

  const amountToPay = request?.approved_amount ?? request?.requested_amount ?? 0;

  return (
    <Modal open={open} onClose={isPaying ? undefined : onClose}>
      <div className="w-full h-full md:h-auto md:w-[90%] lg:w-[60%] max-w-lg bg-white rounded-none md:rounded-2xl shadow-2xl absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 overflow-hidden border-0 md:border md:border-gray-150 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <CreditCardIcon className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-bold text-gray-800">Mark Request as Paid</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isPaying}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {request && (
            <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">Employee:</span>
                <span className="font-bold text-gray-900">{request.employee_name || "-"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">Approved Amount:</span>
                <span className="font-extrabold text-emerald-700 text-base">₹{amountToPay}</span>
              </div>
              {request.requested_amount && request.requested_amount !== amountToPay && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Originally Requested:</span>
                  <span>₹{request.requested_amount}</span>
                </div>
              )}
              <div className="flex justify-between text-xs text-gray-500">
                <span>Period:</span>
                <span>{request.from_date} to {request.to_date}</span>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 leading-relaxed">
            Marking this request as paid will credit <strong className="text-gray-700">₹{amountToPay}</strong> to the employee's petty cash ledger and update the wallet balance.
          </p>

          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1">
              Payment Reference / UTR / Cheque No (Optional)
            </label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="e.g. TXN-984210, UTR-4910283, Cheque #00412"
              className="w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-xs"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1">
              Payment Remarks (Optional)
            </label>
            <textarea
              value={paymentRemarks}
              onChange={(e) => setPaymentRemarks(e.target.value)}
              placeholder="e.g. Released via bank transfer / Accounts petty cash payout"
              rows={3}
              className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none shadow-xs"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPaying}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPaying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isPaying ? (
                <>
                  <span>Processing...</span>
                  <InlineLoader className="ml-2" />
                </>
              ) : (
                "Confirm & Mark as Paid"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default FundRequestMarkAsPaidModal;
